'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import Link, { LinkProps } from 'next/link';
import * as React from 'react';

import { env } from '@/constant/env';
import { getLinkHrefAndRel } from '@/lib/link-config';
import { cn } from '@/lib/utils';

import { HTMLDataTrackerProps, PHTrackerProps } from '@/app/types';
import ButtonIcon from '@/components/atoms/Button/ButtonIcon';
import { returnRefAndGenerateDataTracker } from '@/components/atoms/Button/utils';
import { sharedStyles } from '@/components/atoms/Input';

export const variantsOptions = ['filled', 'outlined', 'text', 'input'] as const;
export type ButtonVariants = (typeof variantsOptions)[number];
export const colorsOptions = [
  'primary',
  'secondary',
  'white',
  'destructive',
  'tertiary',
  'gray',
] as const;
export type ButtonColors = (typeof colorsOptions)[number];
export const sizesOptions = ['l', 'm', 's'] as const;
export type ButtonSizes = (typeof sizesOptions)[number];

const commonStyles = cn(
  'w-fit font-serif font-medium inline-flex gap-2 items-center justify-center rounded-[4px] transition-colors disabled:bg-gray-200 disabled:text-gray-500 disabled:pointer-events-none'
);

const variantColorStyles = {
  filled: {
    primary:
      'text-base-dark-white bg-brand-500 hover:bg-brand-600 active:bg-brand-700',
    secondary:
      'text-base-white bg-gray-900 hover:bg-gray-800 active:bg-gray-700',
    white: 'text-base-black bg-base-white hover:bg-gray-100 active:bg-gray-200',
    destructive:
      'text-base-white bg-error-600 hover:bg-error-700 active:bg-error-800',
    tertiary:
      'text-base-white bg-tertiary-500 hover:bg-tertiary-600 active:bg-tertiary-700',
    gray: 'text-base-white bg-gray-500 hover:bg-brand-600 active:bg-brand-700',
  },
  outlined: {
    primary:
      'text-brand-500 bg-transparent border-brand-500 border boder-solid hover:shadow-border-px hover:shadow-brand-500 active:bg-brand-50 disabled:border-gray-500 disabled:text-gray-500 disabled:bg-transparent',
    secondary:
      'text-base-black bg-transparent border-base-black border boder-solid hover:shadow-border-px hover:shadow-base-black active:bg-gray-100',
    white:
      'text-base-white bg-transparent border-base-white border boder-solid hover:shadow-border-px hover:shadow-base-white active:bg-gray-800',
    destructive:
      'text-error-600 bg-transparent border-error-600 border boder-solid hover:shadow-border-px hover:shadow-error-600 active:bg-error-50',
    tertiary:
      'text-tertiary-500 bg-transparent border-tertiary-500 border boder-solid hover:shadow-border-px hover:shadow-tertiary-500 active:bg-tertiary-50',
    gray: 'text-gray-500 bg-transparent border-brand-500 border boder-solid hover:shadow-border-px hover:text-brand-500 hover:shadow-brand-500 active:bg-base-white disabled:!bg-base-white disabled:!text-brand-500',
  },
  text: {
    primary:
      'text-brand-500 bg-transparent hover:bg-brand-50 active:bg-brand-100',
    secondary:
      'text-base-black bg-transparent hover:bg-gray-50 active:bg-gray-200',
    white:
      'text-base-white bg-transparent hover:bg-gray-800 active:bg-gray-700',
    destructive:
      'text-error-600 bg-transparent hover:bg-error-50 active:bg-error-100',
    tertiary:
      'text-tertiary-500 bg-transparent hover:bg-tertiary-50 active:bg-tertiary-100',
    gray: 'text-gray-500 bg-transparent hover:text-brand-500 hover:border hover:border-brand-500 active:bg-base-white',
  },
  input: {
    primary: cn('hover:shadow-none justify-start px-4', sharedStyles),
    secondary: cn('hover:shadow-none justify-start px-4', sharedStyles),
    white: cn('hover:shadow-none justify-start px-4', sharedStyles),
    destructive: cn('hover:shadow-none justify-start px-4', sharedStyles),
    tertiary: cn('hover:shadow-none justify-start px-4', sharedStyles),
    gray: cn('hover:shadow-none justify-start px-4', sharedStyles),
  },
} as const;

const buttonVariants = cva(commonStyles, {
  variants: {
    variant: {
      filled: '',
      outlined: '',
      text: '',
      input: '',
    },
    color: {
      primary: '',
      secondary: '',
      white: '',
      destructive: '',
      tertiary: '',
      gray: '',
    },
    size: {
      l: 'h-12 text-[1.125rem] px-6 lineHeight-[2rem]',
      m: 'h-10 text-[1rem] px-6 lineHeight-[1.5rem]',
      s: 'h-8 text-[0.875rem] px-4 lineHeight-[1rem]',
    },
    slim: {
      true: 'p-0 h-fit',
      false: '',
    },
    fullwidth: {
      true: 'w-full',
      false: '',
    },
  },
  compoundVariants: [
    // Filled variant + colors
    {
      variant: 'filled',
      color: 'primary',
      class: variantColorStyles.filled.primary,
    },
    {
      variant: 'filled',
      color: 'secondary',
      class: variantColorStyles.filled.secondary,
    },
    {
      variant: 'filled',
      color: 'white',
      class: variantColorStyles.filled.white,
    },
    {
      variant: 'filled',
      color: 'destructive',
      class: variantColorStyles.filled.destructive,
    },
    {
      variant: 'filled',
      color: 'tertiary',
      class: variantColorStyles.filled.tertiary,
    },
    {
      variant: 'filled',
      color: 'gray',
      class: variantColorStyles.filled.gray,
    },
    // Outlined variant + colors
    {
      variant: 'outlined',
      color: 'primary',
      class: variantColorStyles.outlined.primary,
    },
    {
      variant: 'outlined',
      color: 'secondary',
      class: variantColorStyles.outlined.secondary,
    },
    {
      variant: 'outlined',
      color: 'white',
      class: variantColorStyles.outlined.white,
    },
    {
      variant: 'outlined',
      color: 'destructive',
      class: variantColorStyles.outlined.destructive,
    },
    {
      variant: 'outlined',
      color: 'tertiary',
      class: variantColorStyles.outlined.tertiary,
    },
    {
      variant: 'outlined',
      color: 'gray',
      class: variantColorStyles.outlined.gray,
    },
    // Text variant + colors
    {
      variant: 'text',
      color: 'primary',
      class: variantColorStyles.text.primary,
    },
    {
      variant: 'text',
      color: 'secondary',
      class: variantColorStyles.text.secondary,
    },
    {
      variant: 'text',
      color: 'white',
      class: variantColorStyles.text.white,
    },
    {
      variant: 'text',
      color: 'destructive',
      class: variantColorStyles.text.destructive,
    },
    {
      variant: 'text',
      color: 'tertiary',
      class: variantColorStyles.text.tertiary,
    },
    {
      variant: 'text',
      color: 'gray',
      class: variantColorStyles.text.gray,
    },
    // Input variant + colors
    {
      variant: 'input',
      color: 'primary',
      class: variantColorStyles.input.primary,
    },
    {
      variant: 'input',
      color: 'secondary',
      class: variantColorStyles.input.secondary,
    },
    {
      variant: 'input',
      color: 'white',
      class: variantColorStyles.input.white,
    },
    {
      variant: 'input',
      color: 'destructive',
      class: variantColorStyles.input.destructive,
    },
    {
      variant: 'input',
      color: 'tertiary',
      class: variantColorStyles.input.tertiary,
    },
    {
      variant: 'input',
      color: 'gray',
      class: variantColorStyles.input.gray,
    },
  ],
  defaultVariants: {
    variant: 'filled',
    color: 'primary',
    size: 'm',
    slim: false,
    fullwidth: false,
  },
});

type ButtonElementProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  as?: 'button';
};
type AhrefElementProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  as?: 'a';
};
type NextLinkElementProps = React.AnchorHTMLAttributes<HTMLAnchorElement> &
  LinkProps & {
    as?: 'nextlink';
  };

export type BaseButtonProps = {
  children: React.ReactNode;
  variant?: ButtonVariants;
  size?: ButtonSizes;
  color?: ButtonColors;
  slim?: boolean;
  fullwidth?: boolean;
  textWrap?: boolean;
  noStyles?: boolean;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  classNames?: {
    leftSection?: string;
    rightSection?: string;
    inner?: string;
    root?: string;
  };
  asChild?: boolean;
};

export type ButtonProps = (
  | ButtonElementProps
  | AhrefElementProps
  | NextLinkElementProps
) &
  BaseButtonProps &
  HTMLDataTrackerProps &
  PHTrackerProps &
  VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      children,
      variant = 'filled',
      size = 'm',
      color = 'primary',
      slim = false,
      fullwidth = false,
      textWrap = false,
      noStyles = false,
      leftSection,
      rightSection,
      classNames,
      as = 'button',
      asChild = false,
      className,
      ...props
    },
    ref
  ) => {
    const isChildrenValid =
      React.Children.toArray(children).filter((child) => !!child).length > 0;

    const content = (
      <>
        {!!leftSection && (
          <ButtonIcon className={classNames?.leftSection}>
            {leftSection}
          </ButtonIcon>
        )}
        {isChildrenValid && (
          <span
            className={cn(
              !noStyles && 'flex h-full items-center justify-center',
              !noStyles && !textWrap && 'whitespace-nowrap',
              classNames?.inner
            )}
          >
            {children}
          </span>
        )}
        {!!rightSection && (
          <ButtonIcon className={classNames?.rightSection}>
            {rightSection}
          </ButtonIcon>
        )}
      </>
    );

    const conditionalClasses = cn(
      !noStyles && buttonVariants({ variant, color, size, slim, fullwidth }),
      className,
      classNames?.root
    );

    if (as === 'nextlink') {
      return (
        <Link
          prefetch={env.prefetch === 'true'}
          data-tracker-type='link'
          className={conditionalClasses}
          ref={(_ref) => {
            returnRefAndGenerateDataTracker(
              _ref,
              ref as React.Ref<HTMLAnchorElement>
            );
          }}
          {...(props as NextLinkElementProps)}
        >
          {content}
        </Link>
      );
    }

    if (as === 'a') {
      const anchorProps = props as AhrefElementProps;
      const href = String(anchorProps.href ?? '');
      const hasExplicitRel = Object.prototype.hasOwnProperty.call(
        anchorProps,
        'rel'
      );
      const resolved = getLinkHrefAndRel(href);

      return (
        <a
          data-tracker-type='link'
          className={conditionalClasses}
          ref={(_ref) => {
            returnRefAndGenerateDataTracker(
              _ref,
              ref as React.Ref<HTMLAnchorElement>
            );
          }}
          {...anchorProps}
          href={hasExplicitRel ? href : resolved.href}
          rel={hasExplicitRel ? anchorProps.rel : resolved.rel}
        >
          {content}
        </a>
      );
    }

    if (asChild) {
      return (
        <Slot
          className={conditionalClasses}
          ref={ref as React.Ref<HTMLButtonElement>}
          {...(props as ButtonElementProps)}
        >
          {content}
        </Slot>
      );
    }

    return (
      <button
        data-tracker-type='button'
        className={conditionalClasses}
        ref={(_ref) => {
          returnRefAndGenerateDataTracker(
            _ref,
            ref as React.Ref<HTMLButtonElement>
          );
        }}
        {...(props as ButtonElementProps)}
      >
        {content}
      </button>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
